import { api } from './api';

import type { FieldPermissionMatrix } from '../types/fieldPermissions';

interface FieldPermissionMatrixResponse {
  success: boolean;
  stage: string;
  matrix: FieldPermissionMatrix;
}

export interface RolePermissionsResponse {
  success: boolean;
  rolePermissions: Record<string, string[]>;
}

export class PermissionsService {
  /**
   * Fetches the field-permission matrix for a canonical stage. Does NOT catch/swallow errors —
   * fieldPermissionService.fetchPermissionsData relies on this rejecting so its own fallback
   * chain (offline cache -> bundled JSON) can engage. Throws if the response is 200 but missing
   * a matrix (defensive — a malformed 200 should be treated as a failure by the caller, same as
   * a network error or 404).
   */
  static async fetchFieldPermissionMatrix(
    stage: string,
  ): Promise<FieldPermissionMatrix> {
    const response = await api.get<FieldPermissionMatrixResponse>(
      `/permissions/fields/${stage}`,
    );
    if (!response || !response.success || !response.matrix) {
      throw new Error(
        `Invalid field permission matrix response for stage "${stage}"`,
      );
    }
    return response.matrix;
  }

  /**
   * Fetches the coarse role->permissionKey[] map. Does NOT catch/swallow errors — the
   * AuthContext hydration effect relies on this rejecting so its own fallback chain (offline
   * cache -> bundled defaults, already the default state) can engage.
   */
  static async fetchRolePermissions(): Promise<Record<string, string[]>> {
    const response = await api.get<RolePermissionsResponse>(
      '/permissions/roles',
    );
    if (!response || !response.success || !response.rolePermissions) {
      throw new Error('Invalid role permissions response');
    }
    return response.rolePermissions;
  }
}
